'use client';

import {
  type ChangeEvent,
  type FC,
  type FormEvent,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useRouter, useSearchParams } from 'next/navigation';
import {
  Button,
  ButtonGroup,
  Flex,
  Input,
  Separator,
  Text,
} from '@/_components';
import { setSignInMethod } from '@/_api/users';
import { LoginContext } from '@/_contexts/LoginProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { useSignInWithCode } from '@/_hooks/useSignInWithCode';
import { isSameOriginUrl } from '@/_utils/validations';
import type { SetEmailProps } from './SetEmail.types';

export const SetEmail: FC<SetEmailProps> = ({ onBackOverride }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const { shouldShow2FA, send2FAandSetState } = useSignInWithCode();
  const searchParams = useSearchParams();

  const { previousLoginState, setLoginState } = useContext(LoginContext);
  const { addToast } = useContext(ToastContext);
  const { setUser, token, user } = useContext(UserContext);
  const { push } = useRouter();

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const onBack = () => {
    if (onBackOverride) return onBackOverride();

    setLoginState(previousLoginState || 'login');
  };

  useEffect(() => {
    if (emailError) {
      addToast('error', emailError);
    }
  }, [addToast, emailError]);

  const validateEmail = (email: string) => {
    let error = '';

    if (!email) error = 'Email is required';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) error = 'Invalid email address';

    setEmailError(error);
    return error;
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const error = validateEmail(email);

    if (error) return setIsLoading(false);

    setIsLoading(true);

    try {
      const redirect: string | null = searchParams.get('redirect');
      const { error } = await setSignInMethod({
        signInMethod: 'email',
        email,
        token,
      });

      if (!error) {
        setUser({ ...user!, set_password_required: false });
      }

      if (shouldShow2FA) {
        return send2FAandSetState();
      }

      if (!error) {
        const redirectUrl =
          redirect && isSameOriginUrl(redirect) ? redirect : '/';
        return push(redirectUrl);
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onEmailChange = (event: ChangeEvent<HTMLInputElement>) =>
    setEmail(event.target.value?.trim()?.toLowerCase());

  return (
    <form onSubmit={onSubmit}>
      <Flex isColumn={true} gap={8} padding={10}>
        <Text type="small">
          We will send you a <strong>passcode</strong> to the following email
          whenever you sign in.
        </Text>
        <Input
          label="Your email"
          value={email}
          onChange={onEmailChange}
          type="email"
          placeholder="Enter your email"
        />
        <Separator isLight={true} />
        <ButtonGroup align="space-between" className="flex-row">
          <Button
            iconLeft="arrow-left"
            intent="ghost"
            onClick={onBack}
            type="button"
            value="Back"
          />
          <Button
            isDisabled={isLoading}
            isLoading={isLoading}
            intent="primary"
            type="submit"
            value="Submit"
          />
        </ButtonGroup>
      </Flex>
    </form>
  );
};
